import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun, User, Globe } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { NavLink } from '../../utils/NavLink';
import { useAuth } from '../../context/AuthContext';
import { UserMenu } from './UserMenu';

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  // Mise à jour pour utiliser currentUser au lieu de user
  const { currentUser } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Mise à jour du log pour refléter le changement
  console.log('Header render - currentUser:', currentUser); 

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem('lang', language);
    setIsLanguageMenuOpen(false);
  };

  // Fonction pour gérer le clic sur le bouton utilisateur
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Éviter la propagation des événements
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Gérer le clic en dehors du menu pour le fermer
  React.useEffect(() => {
    const handleClickOutside = () => {
      setUserMenuAnchor(null);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fonction pour obtenir le prénom - on la conserve mais elle est utilisée différemment maintenant
  const getFirstName = (fullName: string | undefined) => {
    if (!fullName) return '';
    const trimmedName = fullName.trim();
    if (!trimmedName) return '';
    const firstName = trimmedName.split(' ')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
  };

  // Simplifier la vérification de connexion - mise à jour pour utiliser currentUser
  const isLoggedIn = Boolean(currentUser?.id);
  // Utilisation de la fonction getFirstName avec currentUser
  const displayName = currentUser?.name ? getFirstName(currentUser.name) : '';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-black">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo à gauche */}
          <NavLink to="home" className="flex items-center">
            <img
              src={theme === 'dark' ? '/img/logo_header_dark.png' : '/img/logo_header.png'}
              alt="Ad Vitad"
              className="h-16 w-auto"
              style={{ clipPath: 'inset(0 0 1px 0)' }}
            />
          </NavLink>

          {/* Menu utilisateur à droite */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button 
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-expanded={isLanguageMenuOpen}
              >
                <Globe className="h-5 w-5 text-gray-700 dark:text-gray-200" />
              </button>
              {isLanguageMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-black dark:from-black dark:to-black ring-1 ring-black ring-opacity-5">
                  <div className="py-1">
                    <button
                      onClick={() => changeLanguage('en')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {t('header.language.english')}
                    </button>
                    <button
                      onClick={() => changeLanguage('fr')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {t('header.language.french')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 text-gray-200" />
              ) : (
                <Moon className="h-5 w-5 text-gray-700" />
              )}
            </button>

            {/* Login/Dashboard Button */}
            {isLoggedIn ? (
              <>
                <button
                  onClick={handleUserMenuClick}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full 
                          bg-transparent text-black hover:bg-blue-600 hover:text-white 
                          dark:bg-white dark:text-black dark:hover:bg-blue-600 dark:hover:text-white 
                          transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span className="text-sm font-medium">{displayName}</span>
                </button>
                {userMenuAnchor && (
                  <UserMenu
                    anchorEl={userMenuAnchor}
                    onClose={handleUserMenuClose}
                  />
                )}
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full 
                        bg-transparent text-black hover:bg-blue-600 hover:text-white 
                        dark:bg-white dark:text-black dark:hover:bg-blue-600 dark:hover:text-white 
                        transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">{t('header.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}